import { ReactNode } from 'react';

import { IconAiAgent } from '@components/icons/ai-agent';
import { IconList } from '@components/icons/list';
import { IconTraveller } from '@components/icons/traveller';
import { getScopedTranslate } from '@locale/translate';

import { usePermissions } from 'contexts/permissions-context';
import { JourneyTemplate } from 'ui/unauthenticated/pages/templates/journey-template';

import { AIAgentList } from './pages/ai-agents/list';
import { CompanyTravellerList } from './pages/company-travellers/list';
import { JourneyDetails } from './pages/journeys/journey-details';
import { JourneyEdit } from './pages/journeys/journey-edit';
import { JourneyList } from './pages/journeys/journey-list';
import { TravellerPage } from './pages/travellers/traveller-page';

import { AccessDenied } from '../access-denied';
import { DocumentDetail } from '../settings/pages/documents/detail';
import { DocumentsList } from '../settings/pages/documents/list';
import { CompanyLists } from '../settings/pages/organization/lists/list';

const locale = {
  en: {
    lists: 'Lists',
    travellerProfiles: 'Traveller profiles',
    aiAgents: 'AI agents',
  },
};

const { t } = getScopedTranslate(locale);

const AdminOrEditorGuard = ({ children }: { children: ReactNode }) => {
  const { canViewTravellerProfiles } = usePermissions();

  if (!canViewTravellerProfiles) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

export const mainRoutes = [
  {
    name: 'workspaceJourneyList',
    path: 'workspaces/:workspaceId/journeys',
    page: <JourneyList />,
  },
  {
    commandPaletteName: t('travellerProfiles'),
    commandPaletteIcon: IconTraveller,
    name: 'travellersList',
    path: 'travellers/*',
    page: (
      <AdminOrEditorGuard>
        <CompanyTravellerList />
      </AdminOrEditorGuard>
    ),
  },
  {
    name: 'createJourney',
    path: 'workspaces/:workspaceId/journeys/create/*',
    page: <JourneyEdit />,
    hideNavbar: true,
  },
  {
    name: 'editJourney',
    path: 'workspaces/:workspaceId/journeys/:journeyId/edit/*',
    page: <JourneyEdit />,
    hideNavbar: true,
  },
  {
    name: 'journeyDetails',
    path: 'workspaces/:workspaceId/journeys/:journeyId/*',
    page: <JourneyDetails />,
  },
  {
    name: 'traveller',
    path: 'workspaces/:workspaceId/journeys/:journeyId/travellers/:travellerId/*',
    page: <TravellerPage />,
  },
  {
    commandPaletteName: t('lists'),
    commandPaletteIcon: IconList,
    name: 'companyLists',
    path: 'lists',
    page: (
      <AdminOrEditorGuard>
        <CompanyLists />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('aiAgents'),
    commandPaletteIcon: IconAiAgent,
    name: 'aiAgents',
    path: 'ai-agents',
    page: (
      <AdminOrEditorGuard>
        <AIAgentList />
      </AdminOrEditorGuard>
    ),
  },
  {
    name: 'workspaceDocuments',
    path: 'workspaces/:workspaceId/documents',
    page: <DocumentsList />,
  },
  {
    name: 'worksspaceDocumentDetail',
    path: 'workspaces/:workspaceId/documents/:documentId/*',
    page: <DocumentDetail />,
  },
  {
    name: 'templates',
    path: 'templates/:journeyId/',
    page: <JourneyTemplate />,
    hideNavbar: true,
  },
] as const;
